/* Practice trainer logic: sampling, grading, filtering, table narration.
   No DOM, no data — everything here takes what it needs as an argument, so the same code
   runs in the page and under Vitest. */

import { ORDER, SUITS, combosOf, type Action, type Suit } from "./hands";
import { versus } from "./opponents";
import { actionLabel } from "./rules";
import { SEATS, seatName } from "./seats";
import type { Opponent } from "./opponents";
import type { Bets, Cell, Charted, SpotPlace } from "./spot";

/* Grading is a dice roll, not a threshold. The chart's frequencies are laid out as bands
   over 0-99 and the roll picks the one to play, so a mixed hand has a single right answer
   on this deal. Bands run from the most passive action up to the most aggressive: a high
   roll always means get it in. A roll landing this close to a band edge takes the neighbour
   too, because being one point off a boundary is not a misread of the chart. */
export const RND_MAX = 100;
export const GRACE = 5;
export const PARTIAL = 0.25; // a mixed line played the wrong way
export const AGGRO = ["fold", "call", "raise", "allin"] as const; // ascending aggression

/** What the trainer reads off a spot: where it sits, its chart, and what the bets cost. */
export type TableSpot = Omit<SpotPlace, "stakes" | "gameType" | "format"> &
  Partial<Pick<SpotPlace, "stakes" | "gameType" | "format">> & { bets?: Bets | undefined };
export type DrillSpot = TableSpot & Charted;

/** What the setup screen was set to. Empty means no filter on that rung. */
export interface PracticeConfig {
  stakes?: string | null;
  stack?: string | null;
  openSize?: string | null;
  actions?: string[] | null;
  /** "edge" keeps the hands around the limit; anything else asks the whole range. */
  pool?: string | null;
  /** "mistakes" deals the charts that have been missed more often; anything else, alike. */
  focus?: string | null;
  /* Whether a chart's variations are dealt beside it. Off, the default, only the charts
     written against nobody in particular come up, and a session is what it was before
     variations existed. On, each variation is an exercise of its own: it comes up as often
     as any other chart the filters match, and it is what the answer is graded against. */
  variations?: boolean;
  count?: number;
}

export type Rng = () => number;

/* ---- sampling ---- */

/** Deterministic PRNG (mulberry32), so a session can be replayed from a seed. */
export function seeded(seed: number): Rng {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* A chart is mostly hands that fold themselves, and being asked 72o forty times teaches
   nothing. The edge pool keeps what the chart plays plus the folds sitting just under the
   limit, which is where the reading is hard. */
export const EDGE_STEPS = 2;

const RANK_AT = "AKQJT98765432";

/* Hands sit on a line of their own shape: a pair among the pairs, a suited or offsuit hand
   among the ones sharing its high card, ordered by kicker. The line never crosses between
   pairs, suited and offsuit — A4s is next to A5s, K5s and A4o are not. */
const lineOf = (hand: string): { key: string; at: number } =>
  hand.length === 2
    ? { key: "pair", at: RANK_AT.indexOf(hand.charAt(0)) }
    : { key: hand.charAt(0) + hand.charAt(2), at: RANK_AT.indexOf(hand.charAt(1)) };

const played = (cell: Cell): boolean => ORDER.some((a) => a !== "fold" && (cell[a] ?? 0) > 0);

/**
 * The hands worth being asked: everything the chart plays, plus the folds within `steps` of
 * one along its own line. A chart that plays nothing at all falls back to every hand it
 * holds, so a spot can never be undrillable.
 */
export function poolHands(spot: Charted, steps = EDGE_STEPS): string[] {
  const all = spot.baseInRange ?? Object.keys(spot.cells);
  const live = new Map<string, number[]>(); // line -> the positions played on it
  for (const h of all) {
    if (!played(spot.cells[h] ?? {})) continue;
    const { key, at } = lineOf(h);
    const line = live.get(key) ?? [];
    line.push(at);
    live.set(key, line);
  }
  if (!live.size) return all;
  return all.filter((h) => {
    const { key, at } = lineOf(h);
    return (live.get(key) ?? []).some((p) => Math.abs(p - at) <= steps);
  });
}

/**
 * How often hero holds each hand here, in combos. A spot's `cells` is already hero's range —
 * 169 classes for an open, 28 facing a 4-bet — and `range` thins the ones hero reaches with
 * only part of its combos. `cfg.pool` narrows that again to the hands around the limit.
 */
export function handWeights(spot: Charted, cfg?: PracticeConfig | null): Map<string, number> {
  const out = new Map<string, number>();
  const hands =
    cfg?.pool === "edge" ? poolHands(spot) : (spot.baseInRange ?? Object.keys(spot.cells));
  for (const h of hands) {
    const p = spot.range?.[h] ?? 1;
    if (p > 0) out.set(h, combosOf(h) * p);
  }
  return out;
}

/** One hand from the spot, in proportion to the combos hero holds of it. */
export function pickHand(spot: Charted, rng: Rng, cfg?: PracticeConfig | null): string | null {
  const w = handWeights(spot, cfg);
  let total = 0;
  for (const n of w.values()) total += n;
  let r = rng() * total;
  let last: string | null = null;
  for (const [h, n] of w) {
    last = h;
    if ((r -= n) < 0) return h;
  }
  return last; // float dust at the very top end
}

const pick = <T>(list: readonly T[], rng: Rng): T | undefined =>
  list[Math.min(list.length - 1, Math.floor(rng() * list.length))];

/** One of the list, in proportion to its weight. One roll of the generator, as `pick` takes,
    so a deal is the same length of stream whether it is weighted or not. */
function pickWeighted<T>(
  list: readonly T[],
  rng: Rng,
  weightOf: (item: T) => number,
): T | undefined {
  // A weight that is no weight (zero, negative, not a number) is 1: an item is never shut out.
  const weights = list.map((item) => {
    const w = weightOf(item);
    return Number.isFinite(w) && w > 0 ? w : 1;
  });
  let r = rng() * weights.reduce((sum, w) => sum + w, 0);
  for (let i = 0; i < list.length; i++) if ((r -= weights[i] ?? 0) < 0) return list[i];
  return list[list.length - 1]; // float dust at the very top end
}

/* Drilling one's mistakes: a chart that has been costing points comes up more often, up to
   five times as often as one that has not. The two hands added underneath keep a rate from
   being believed before it has been seen a few times: one miss in one hand is not the chart
   missed twenty times in twenty. A chart never missed, or never seen, weighs 1 and still
   comes up. */
export const MISTAKE_BOOST = 4;
export const MISTAKE_PRIOR = 2;
export const mistakeWeight = (hands: number, points: number): number =>
  hands > 0 ? 1 + (MISTAKE_BOOST * Math.max(0, hands - points)) / (hands + MISTAKE_PRIOR) : 1;

export interface Card {
  rank: string;
  suit: Suit;
}

/** Two suits that are not the same one, drawn without replacement. */
function twoSuits(rng: Rng): [Suit, Suit] {
  const a = Math.min(3, Math.floor(rng() * 4));
  const rest = SUITS.filter((_, i) => i !== a);
  return [SUITS[a] ?? "s", rest[Math.min(2, Math.floor(rng() * 3))] ?? "h"];
}

/** A hand class -> the concrete cards to show, higher rank first. */
export function dealSuits(hand: string, rng: Rng): [Card, Card] {
  const [hi, lo] = [hand.charAt(0), hand.charAt(1)];
  if (hand.length === 3 && hand.charAt(2) === "s") {
    const s = SUITS[Math.min(3, Math.floor(rng() * 4))] ?? "s";
    return [
      { rank: hi, suit: s },
      { rank: lo, suit: s },
    ];
  }
  const [x, y] = twoSuits(rng);
  return [
    { rank: hi, suit: x },
    { rank: lo, suit: y },
  ];
}

/* ---- grading ---- */

export interface Band {
  action: Action;
  lo: number;
  hi: number;
}

/** The hand's strategy as bands over 0-100, passive first. */
export function bands(cell: Cell): Band[] {
  const live = AGGRO.filter((a) => (cell[a] ?? 0) > 0);
  let total = 0;
  for (const a of live) total += cell[a] ?? 0;
  if (!total) return []; // a cell with nothing in it
  let lo = 0;
  return live.map((a) => {
    const hi = lo + (RND_MAX * (cell[a] ?? 0)) / total;
    const band = { action: a, lo, hi };
    lo = hi;
    return band;
  });
}

/** The roll shown beside the hand: one integer in 0-99. */
export const rollRnd = (rng: Rng): number => Math.min(RND_MAX - 1, Math.floor(rng() * RND_MAX));

/**
 * What this roll defends: the band it lands in, plus a neighbour whenever it lands within
 * `grace` of the boundary they share. Returned in the app's action order so the answer
 * buttons and the recap read the same way.
 */
export function correctActions(cell: Cell, rnd = 0, grace = GRACE): Action[] {
  const bs = bands(cell);
  const i = bs.findIndex((b) => rnd < b.hi);
  const at = i < 0 ? bs.length - 1 : i; // float dust past the top
  const here = bs[at];
  if (!here) return [];
  const out = new Set<Action>([here.action]);
  const below = bs[at - 1];
  const over = bs[at + 1];
  if (below && rnd - here.lo <= grace) out.add(below.action);
  if (over && here.hi - rnd <= grace) out.add(over.action);
  return ORDER.filter((a) => out.has(a));
}

export const isCorrect = (cell: Cell, answer: Action, rnd = 0, grace = GRACE): boolean =>
  correctActions(cell, rnd, grace).includes(answer);

/**
 * What an answer is worth, so the session score reads as accuracy rather than a count of
 * hands. A full point when the roll defends the answer. A quarter of one when the chart
 * does take that line here and the roll simply went the other way: a mixed hand played the
 * minority way is a misread of the roll, not of the hand. Nothing for a line the chart
 * never takes, however the roll came down.
 */
export const scoreAnswer = (cell: Cell, answer: Action, rnd = 0, grace = GRACE): number =>
  isCorrect(cell, answer, rnd, grace) ? 1 : (cell[answer] ?? 0) > 0 ? PARTIAL : 0;

/**
 * The rolls an action owns, as the two integers at its ends. A band runs from its low edge
 * up to but not including its high one, so a raise sitting on 40-100 is every roll from 40
 * to 99. An action the hand never takes has no rolls and comes back null.
 */
export function rollRange(cell: Cell, action: Action): [number, number] | null {
  const b = bands(cell).find((x) => x.action === action);
  if (!b) return null;
  // Float dust can push the top band a hair past 100; the roll never goes there.
  const lo = Math.max(0, Math.ceil(b.lo));
  const hi = Math.min(RND_MAX - 1, Math.ceil(b.hi) - 1);
  return hi < lo ? null : [lo, hi]; // a band too thin to hold one
}

/* ---- filtering ---- */

type Filterable = Pick<SpotPlace, "action"> &
  Partial<Pick<SpotPlace, "stakes" | "stack" | "openSize" | "profile">>;
type FilterDim = "stakes" | "stack" | "openSize" | "action";

const FILTERS: Record<FilterDim, (s: Filterable, c: PracticeConfig) => boolean> = {
  stakes: (s, c) => !c.stakes || s.stakes === c.stakes,
  stack: (s, c) => !c.stack || s.stack === c.stack,
  openSize: (s, c) => !c.openSize || s.openSize === c.openSize,
  action: (s, c) => !c.actions || !c.actions.length || c.actions.includes(s.action),
};
const FILTER_DIMS = Object.keys(FILTERS) as FilterDim[];

/* The variations switch is not a rung of the filter grid — it is about which charts exist to
   be filtered at all — so it stands outside the dimensions the rows count against. Off, a
   variation is not a chart this session knows about. */
const dealt = (s: Filterable, c: PracticeConfig): boolean => !s.profile || c.variations === true;

export const matchingSpots = <S extends Filterable>(
  spots: readonly S[],
  cfg: PracticeConfig,
): S[] => spots.filter((s) => dealt(s, cfg) && FILTER_DIMS.every((k) => FILTERS[k](s, cfg)));

/** Whether this collection holds a variation at all: with none there is no switch to draw. */
export const hasVariations = (spots: readonly Filterable[]): boolean =>
  spots.some((s) => !!s.profile);

/**
 * How many charts each action holds, with the other filters standing. Written on the action
 * buttons so the reach of a pick is visible before it is made.
 */
export function actionCounts(
  spots: readonly Filterable[],
  cfg: PracticeConfig,
): Map<string, number> {
  const others = FILTER_DIMS.filter((k) => k !== "action");
  const out = new Map<string, number>();
  for (const s of spots)
    if (dealt(s, cfg) && others.every((k) => FILTERS[k](s, cfg)))
      out.set(s.action, (out.get(s.action) ?? 0) + 1);
  return out;
}

/**
 * What each filter could still be switched to, holding the other choices. Values missing
 * from these sets have no chart behind them and are shown disabled rather than hidden, so
 * gaps in the collection stay visible.
 */
export function availableFor(
  spots: readonly Filterable[],
  cfg: PracticeConfig,
): Record<FilterDim, Set<string | undefined>> {
  const out: Record<FilterDim, Set<string | undefined>> = {
    stakes: new Set(),
    stack: new Set(),
    openSize: new Set(),
    action: new Set(),
  };
  for (const dim of FILTER_DIMS) {
    const others = FILTER_DIMS.filter((k) => k !== dim);
    for (const s of spots)
      if (dealt(s, cfg) && others.every((k) => FILTERS[k](s, cfg))) out[dim].add(s[dim]);
  }
  return out;
}

/* ---- session ---- */

export interface Exercise<S extends DrillSpot = DrillSpot> {
  spot: S;
  hand: string;
  cards: [Card, Card];
  cell: Cell;
  rnd: number;
  correct: Action[];
}

/**
 * The whole exercise list, drawn up front. Fixing it means the recap can point back at
 * spots that really were asked, and a retry can ask them again. With `weightOf`, a chart
 * comes up in proportion to its weight; without, every chart alike.
 */
export function buildSession<S extends DrillSpot>(
  spots: readonly S[],
  cfg: PracticeConfig,
  rng: Rng,
  weightOf?: (spot: S) => number,
): Exercise<S>[] {
  const pool = matchingSpots(spots, cfg);
  const out: Exercise<S>[] = [];
  for (let i = 0; i < (cfg.count ?? 0); i++) {
    const spot = weightOf ? pickWeighted(pool, rng, weightOf) : pick(pool, rng);
    if (!spot) break;
    const hand = pickHand(spot, rng, cfg);
    if (hand === null) continue;
    const cell = spot.cells[hand] ?? {};
    const cards = dealSuits(hand, rng);
    const rnd = rollRnd(rng);
    out.push({ spot, hand, cards, cell, rnd, correct: correctActions(cell, rnd) });
  }
  return out;
}

/* ---- the table ---- */

type BetName = "raise" | "call" | "3bet" | "4bet" | "5bet";

/* What the seats did before it got back to hero. Hero's own earlier bet is part of the
   story wherever hero has already acted: facing a 3-bet is only readable if the raise hero
   made is on the table too. */
const HERO_BET: Readonly<Record<string, "open" | BetName>> = {
  "vs. 3 Bet": "open",
  "vs. 4 Bet": "3bet",
  "vs. 5 Bet": "4bet",
  "vs. Squeeze": "open",
  "Called vs. Squeeze": "call",
};
const OPP_STATE: Readonly<Record<string, readonly BetName[]>> = {
  "vs. RFI": ["raise"],
  "vs. 3 Bet": ["3bet"],
  "vs. 4 Bet": ["4bet"],
  "vs. 5 Bet": ["5bet"],
  "Cold 4bet": ["raise", "3bet"],
  "vs. Raise-Call": ["raise", "call"],
  "vs. Squeeze": ["call", "3bet"],
  "Called vs. Squeeze": ["raise", "3bet"],
};
const CHIP: Readonly<Record<BetName, string>> = {
  raise: "Raise",
  call: "Call",
  "3bet": "3-Bet",
  "4bet": "4-Bet",
  "5bet": "All-In",
};

/* What the opener did to the raise that came in behind it, in the spots where it answered
   before hero. Calling means matching that raise, so it puts the same money in; folding
   leaves what it had already bet behind as dead. */
const BRANCH_PLAY: Readonly<Record<string, { state: SeatState; bet: BetName | null }>> = {
  Called: { state: "call", bet: "3bet" },
  Folded: { state: "folded", bet: null },
  "4-Bet": { state: "4bet", bet: "4bet" },
  Jammed: { state: "5bet", bet: "5bet" },
};

/* ---- money on the table ---- */

export const BLINDS: Readonly<Record<string, number>> = { SB: 0.5, BB: 1 };

/* Position is a postflop question: the blinds pay first preflop but act first on every
   street after it, so they are out of position on the whole field. */
const POSTFLOP = ["SB", "BB", "LJ", "HJ", "CO", "BTN"];
const inPosition = (a: string, b: string): boolean => POSTFLOP.indexOf(a) > POSTFLOP.indexOf(b);
const half = (n: number): number => Math.round(n * 2) / 2;
/** An amount in blinds, written the way the table writes it: 2.5, not 2.50. */
export const blinds = (n: number): string => String(Math.round(n * 10) / 10);

/**
 * What each bet on this street costs. A chart out of the base collection carries the sizes
 * it was solved at in `bets`, read off the titles of its source — a 3-bet is 7.5bb from the
 * button and 12 from the big blind, and no formula gets both. A chart written in the editor
 * has none, so those fall back to the way a 100bb table prices raises: a 3-bet is 3x the
 * open in position and 4x out of it, a 4-bet 2.2x the 3-bet in position and 2.5x out of it.
 * Rounded to the half blind, which is the smallest chip on the table.
 */
export function betSizes(spot: TableSpot): Record<BetName, number> {
  const said = spot.bets ?? {};
  const open = said.raise ?? (parseFloat(spot.openSize) || 0);
  const s = spot.seats ?? [];
  let opener: string | undefined;
  let threeBettor: string | undefined;
  switch (spot.action) {
    // Hero is the one who would 3-bet here, so hero's seat is what prices it.
    case "vs. RFI":
    case "vs. Raise-Call":
      opener = s[0];
      threeBettor = spot.hero;
      break;
    // A squeeze is the 3-bet of its pot: hero opened and the last seat named is the one
    // that raised over the call.
    case "vs. 3 Bet":
    case "vs. Squeeze":
      opener = spot.hero;
      threeBettor = s[s.length - 1];
      break;
    case "vs. 4 Bet":
      opener = s[0];
      threeBettor = spot.hero;
      break;
    case "vs. 5 Bet":
      opener = spot.hero;
      threeBettor = s[0];
      break;
    case "Cold 4bet":
    case "Called vs. Squeeze":
      opener = s[0];
      threeBettor = s[1];
      break;
  }
  const three =
    said["3bet"] ??
    (threeBettor ? half(open * (inPosition(threeBettor, opener ?? "") ? 3 : 4)) : 0);
  const four =
    said["4bet"] ??
    (threeBettor ? half(three * (inPosition(opener ?? "", threeBettor) ? 2.2 : 2.5)) : 0);
  // A 5-bet at these depths is the stack: there is nothing left to size.
  const five = said["5bet"] ?? half(parseFloat(spot.stack) || 0);
  return { raise: open, call: open, "3bet": three, "4bet": four, "5bet": five };
}

/* Denominations a live table would actually push out. A deep stack would bury the seat
   under a column of ones, so the picture is capped and the smallest chips are the ones that
   drop off it — the amount is written underneath. */
export const DENOMS = [100, 25, 10, 5, 1, 0.5] as const;
const MAX_CHIPS = 8;

/** An amount in blinds as the chips that make it up, biggest first. */
export function chipStack(amount: number | null | undefined): number[] {
  const out: number[] = [];
  let left = half(amount || 0);
  for (const d of DENOMS)
    while (left >= d - 1e-9) {
      out.push(d);
      left = half(left - d);
    }
  return out.slice(0, MAX_CHIPS);
}

export type SeatState = "hero" | "waiting" | "folded" | BetName;

export interface SeatStory {
  seat: string;
  state: SeatState;
  isHero: boolean;
  label: string;
  /** Chips still in front of the seat. */
  amount: number;
  /** Chips it left behind when it folded. */
  dead: number;
}

/** Everything committed so far: chips still in front, plus folded blinds. */
export const potOf = (story: readonly Pick<SeatStory, "amount" | "dead">[]): number =>
  half(story.reduce((n, s) => n + (s.amount || 0) + (s.dead || 0), 0));

/** One entry per seat, in acting order: who folded, who bet, where hero sits. */
export function narrate(spot: TableSpot): SeatStory[] {
  const opp = OPP_STATE[spot.action] ?? [];
  const heroBet = HERO_BET[spot.action];
  // Where hero has already bet, the action has been all the way round: nobody behind is
  // still to act. Otherwise the seats after hero have not spoken.
  const closed = Boolean(heroBet);
  const size = betSizes(spot);
  const at = (s: string): number => (SEATS as readonly string[]).indexOf(s);
  // A blind is money already in front, so a raise out of one tops it up rather than being
  // stacked on top of it.
  const put = (seat: string, bet: number): number => Math.max(bet || 0, BLINDS[seat] ?? 0);
  return SEATS.map((seat): SeatStory => {
    if (seat === spot.hero) {
      const bet = heroBet === "open" ? "raise" : heroBet;
      return {
        seat,
        state: "hero",
        isHero: true,
        label: heroBet === "open" ? `Raise ${blinds(size.raise)}` : bet ? CHIP[bet] : "",
        amount: put(seat, bet ? size[bet] : 0),
        dead: 0,
      };
    }
    const i = (spot.seats ?? []).indexOf(seat);
    if (i >= 0) {
      // The opener answered the raise behind it before hero got to speak.
      const answered = i === 0 && spot.branch ? BRANCH_PLAY[spot.branch] : undefined;
      if (answered) {
        const bet = answered.bet;
        return {
          seat,
          state: answered.state,
          isHero: false,
          label: bet ? CHIP[bet === "3bet" ? "call" : bet] : "",
          amount: bet ? put(seat, size[bet]) : 0,
          dead: bet ? 0 : put(seat, size.raise),
        };
      }
      const state = opp[i] ?? "raise";
      return {
        seat,
        state,
        isHero: false,
        label: state === "raise" ? `Raise ${blinds(size.raise)}` : CHIP[state],
        amount: put(seat, size[state]),
        dead: 0,
      };
    }
    const waiting = !closed && at(seat) > at(spot.hero);
    // Folding does not take a blind back: it is swept in as dead money.
    const blind = BLINDS[seat] ?? 0;
    return {
      seat,
      state: waiting ? "waiting" : "folded",
      isHero: false,
      label: "",
      amount: waiting ? blind : 0,
      dead: waiting ? 0 : blind,
    };
  });
}

/* Which bet hero's own raise button makes. Reading down the line: an unopened pot is
   opened, a raise in front is 3-bet, a 3-bet is 4-bet, a 4-bet is jammed on. `vs. 5 Bet` is
   missing because there is nothing left to raise. */
const HERO_RAISE: Readonly<Record<string, keyof Bets>> = {
  RFI: "raise",
  "vs. RFI": "3bet",
  "vs. 3 Bet": "4bet",
  "vs. 4 Bet": "5bet",
  "Cold 4bet": "4bet",
  "vs. Raise-Call": "3bet",
  "vs. Squeeze": "4bet",
  "Called vs. Squeeze": "4bet",
};

/* An opponent that answered the last raise before hero can move the line on again, and then
   it is that answer hero's raise steps down from rather than the raise under it. A 4-bet is
   5-bet; a jam has nothing left over it, which is the one answer that takes hero's raise
   away entirely. Read only where an action has answers at all. */
const BRANCH_RAISE: Readonly<Record<string, keyof Bets | null>> = {
  "4-Bet": "5bet",
  Jammed: null,
};

/**
 * What each action open to hero costs, written the way a table writes a bet: the amount it
 * is made *to*, not the chips added on top of what is already out. So the number on the
 * button matches the chip it would push the seat's stack to, and matches the chips already
 * drawn in front of the other seats. Calling is whatever the biggest bet in front of hero
 * is; folding is free and has no entry.
 */
export function heroCost(spot: TableSpot): { allin: number; raise: number; call: number } {
  const size = betSizes(spot);
  const faced = narrate(spot)
    .filter((s) => !s.isHero)
    .reduce((n, s) => Math.max(n, s.amount || 0), 0);
  const raise = heroRaiseBet(spot.action, spot.branch);
  return { allin: size["5bet"] || 0, raise: raise ? size[raise] || 0 : 0, call: faced };
}

/** Which bet hero's raise makes here, or null where there is nothing left to raise. */
export const heroRaiseBet = (action: string, branch?: string | null): keyof Bets | null =>
  branch && branch in BRANCH_RAISE ? (BRANCH_RAISE[branch] ?? null) : (HERO_RAISE[action] ?? null);

/** What hero's raise is made to here: the size the chart carries, or the table's price. */
export function heroRaiseSize(spot: TableSpot): number | null {
  const bet = heroRaiseBet(spot.action, spot.branch);
  return bet ? betSizes(spot)[bet] || null : null;
}

/** What the table would price hero's raise at, ignoring any size the chart was given. */
export function pricedRaiseSize(spot: TableSpot): number | null {
  const bet = heroRaiseBet(spot.action, spot.branch);
  if (!bet) return null;
  const bets = { ...spot.bets };
  delete bets[bet];
  return betSizes({ ...spot, bets })[bet] || null;
}

/**
 * Keep a size for hero's raise, or null to give it back to the table. The table's own price
 * is never written down: a chart carries a size only where it was given one, so a 3-bet
 * nobody has an opinion about still follows the open it is priced off.
 */
export function setHeroRaise(spot: TableSpot, size: number | null): void {
  const bet = heroRaiseBet(spot.action, spot.branch);
  if (!bet) return;
  const bets: Bets = { ...spot.bets };
  delete bets[bet];
  if (size && size !== pricedRaiseSize(spot)) bets[bet] = size;
  spot.bets = Object.keys(bets).length ? bets : undefined;
}

/* What the table was set to, as opposed to what happened at it. A session can hold one
   setting or many, and that is what decides whether it is said once at the top of a recap
   or on every row. */
export const TABLE_PARTS = ["stakes", "stack", "openSize"] as const;
type TablePart = (typeof TABLE_PARTS)[number];
export type SharedTable = Record<TablePart, string | null>;

/**
 * The table settings every one of these spots shares. A setting the session mixed comes
 * back null: it tells the rows apart, so it belongs on them.
 */
export function sharedTable(spots: readonly Partial<Record<TablePart, string>>[]): SharedTable {
  const out: SharedTable = { stakes: null, stack: null, openSize: null };
  for (const k of TABLE_PARTS) {
    const first = spots[0]?.[k];
    out[k] = first && spots.every((s) => s[k] === first) ? first : null;
  }
  return out;
}

/**
 * The spot as one line, for headers and the mistake recap. `said` is whatever has already
 * been said elsewhere — the settings a recap hoisted into its header — and is left off so
 * the line does not repeat it.
 *
 * A variation ends on the profile it is written against, because a variation named as the
 * chart it deviates from is two rows the reader cannot tell apart. `opponents` is the
 * collection's list, which is where the name comes from.
 */
export function spotLine(
  spot: TableSpot,
  said?: Partial<SharedTable> | null,
  opponents: readonly Opponent[] = [],
): string {
  const parts = [`${seatName(spot.hero)} ${actionLabel(spot.action)}`];
  if ((spot.seats ?? []).length) parts.push(spot.seats.map(seatName).join(", "));
  if (spot.branch) parts.push(`${seatName(spot.seats[0] ?? "")} ${spot.branch.toLowerCase()}`);
  for (const k of TABLE_PARTS) if (!said?.[k]) parts.push(spot[k] ?? "");
  if (spot.profile) parts.push(versus(opponents, spot.profile));
  return parts.join(" · ");
}
